import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Eye, Edit, Phone, 
  Loader2, MessageSquare, Calendar, Clock, CheckCircle, 
  XCircle, Wrench, User, Building2, MapPin, AlertTriangle,
  FileText, Settings, ChevronRight
} from 'lucide-react';
import api from '../../utils/api';
import { getCurrentLocation, buildLocationParams } from '../../utils/location';

const SERVICE_STATUSES = [
  { value: "New", color: "bg-blue-500 text-white" },
  { value: "On Process", color: "bg-yellow-500 text-white" },
  { value: "Closed", color: "bg-green-500 text-white" },
  { value: "Cancelled", color: "bg-red-500 text-white" }
];

const PRIORITY_OPTIONS = [
  { value: "Normal", color: "text-gray-600" },
  { value: "Urgent", color: "text-orange-600" },
  { value: "Emergency", color: "text-red-600" }
];

const MobileServices = () => {
  const { user: authUser } = useAuth();
  // Fallback to localStorage if AuthContext user is not available (mobile app flow)
  const localUser = React.useMemo(() => {
    try {
      const storedUser = localStorage.getItem('suntech_mobile_user') || localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  }, []);
  const user = authUser || localUser;
  
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [engineerFilter, setEngineerFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  
  // Permission checks based on role
  const isAdmin = user?.role === 'Admin';
  const isBDMSE = user?.role === 'BDM & SE';
  const isBackOffice = user?.role === 'Back Office Executive';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const isServiceEngineer = user?.role === 'Service Engineer';
  const isSalesMarketing = user?.role === 'Sales & Marketing';
  
  // Sales & Marketing cannot access services
  const canAccessServices = !isSalesMarketing;
  
  // Service Engineer can only self-assign, others can assign to any engineer
  const canAssignToOthers = !isServiceEngineer;
  
  // Can view all services (not filtered by assignment)
  const canViewAllServices = isAdmin || isBackOffice || isBackOfficeAccounts;
  const canOnlyViewAssigned = isBDMSE || isServiceEngineer;
  
  // Can edit a service
  const canEditService = (service) => {
    if (isAdmin || isBackOffice || isBackOfficeAccounts) return true;
    if (canOnlyViewAssigned) {
      return service.assigned_to === user?.id;
    }
    return false;
  };
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [closeServiceDialogOpen, setCloseServiceDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Customer contracts and licenses for service type
  const [customerContracts, setCustomerContracts] = useState([]);
  const [customerLicenses, setCustomerLicenses] = useState([]);
  
  // Form states
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  const initialFormData = {
    customer_id: '',
    service_type: '',
    description: '',
    priority: 'Normal',
    due_date: '',
    due_time: '',
    assigned_to: '',
    contract_id: '',
    license_id: '',
    device_reference_number: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  const [statusData, setStatusData] = useState({
    status: '',
    notes: ''
  });
  
  const [commentData, setCommentData] = useState({
    comment: '',
    next_visit_date: ''
  });
  
  const [closureData, setClosureData] = useState({
    service_type_category: '', // "Free" or "Paid"
    payment_status: '', // "Paid" or "Pending"
    payment_mode: '',
    payment_notes: '',
    free_service_notes: '',
    service_charge: '',
    due_date: '',
    collected_by: ''
  });

  // Fetch data
  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/services/');
      const sortedServices = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setServices(sortedServices);
    } catch (error) {
      toast.error('Failed to fetch services');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await api.get('/services/engineers/list');
      setEngineers(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchServiceTypes = useCallback(async () => {
    try {
      const response = await api.get('/services/types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCustomerContracts = async (customerId) => {
    try {
      const response = await api.get(`/contracts/customer/${customerId}`);
      // Filter for active contracts
      setCustomerContracts(response.data.filter(c => 
        c.contract_status === 'Active' && 
        c.services_used < c.services_included
      ));
    } catch (error) {
      console.error(error);
      setCustomerContracts([]);
    }
  };

  const fetchCustomerLicenses = async (customerId) => {
    try {
      const response = await api.get(`/licenses/customer/${customerId}/expiring`);
      setCustomerLicenses(response.data);
    } catch (error) {
      console.error(error);
      setCustomerLicenses([]);
    }
  };

  useEffect(() => {
    if (canAccessServices) {
      fetchServices();
      fetchCustomers();
      fetchEngineers();
      fetchServiceTypes();
    }
  }, [canAccessServices, fetchServices, fetchCustomers, fetchEngineers, fetchServiceTypes]);

  // Auto-assign to self for Service Engineer when dialog opens or engineers load
  useEffect(() => {
    if (createDialogOpen && isServiceEngineer && user?.id && engineers.length > 0) {
      // Check if user is in engineers list
      const userInList = engineers.find(e => e.id === user.id);
      if (userInList) {
        setFormData(prev => ({ ...prev, assigned_to: user.id }));
      }
    }
  }, [createDialogOpen, isServiceEngineer, user?.id, engineers]);

  // Calculate status counts
  const statusCounts = services.reduce((acc, service) => {
    // Apply permission filter
    if (canOnlyViewAssigned && service.assigned_to !== user?.id) {
      return acc;
    }
    const status = service.status || 'New';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Filter services based on permissions and search
  const filteredServices = services.filter(service => {
    // Permission filter
    if (canOnlyViewAssigned && service.assigned_to !== user?.id) {
      return false;
    }
    
    // Search filter
    const matchesSearch = 
      service.service_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
    
    // Engineer filter (Admin only)
    const matchesEngineer = engineerFilter === 'all' || service.assigned_to === engineerFilter;
    
    // Service Type filter
    const matchesServiceType = serviceTypeFilter === 'all' || service.service_type === serviceTypeFilter;
    
    return matchesSearch && matchesStatus && matchesEngineer && matchesServiceType;
  });

  // Helper functions
  const getStatusColor = (status) => {
    const found = SERVICE_STATUSES.find(s => s.value === status);
    return found ? found.color : 'bg-gray-500 text-white';
  };

  const getPriorityColor = (priority) => {
    const found = PRIORITY_OPTIONS.find(p => p.value === priority);
    return found ? found.color : 'text-gray-600';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Filtered customers for dropdown
  const filteredCustomers = customers.filter(c =>
    c.company_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.mobile?.includes(customerSearchTerm) ||
    c.contact_person?.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Form handlers
  const resetForm = () => {
    setFormData({
      ...initialFormData,
      assigned_to: isServiceEngineer && user?.id ? user.id : ''
    });
    setCustomerSearchTerm('');
    setCustomerContracts([]);
    setCustomerLicenses([]);
  };

  const handleCustomerSelect = async (customer) => {
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setCustomerSearchTerm(customer.company_name);
    setShowCustomerDropdown(false);
    
    // Fetch contracts and licenses for this customer
    await fetchCustomerContracts(customer.id);
    await fetchCustomerLicenses(customer.id);
  };

  const handleServiceTypeChange = (type) => {
    setFormData(prev => ({ 
      ...prev, 
      service_type: type,
      contract_id: '',
      license_id: '',
      device_reference_number: ''
    }));
  };

  const openCreateDialog = () => {
    resetForm();
    // Set default due date to today and auto-assign for Service Engineer
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({ 
      ...prev, 
      due_date: today,
      assigned_to: isServiceEngineer && user?.id ? user.id : ''
    }));
    setCreateDialogOpen(true);
  };

  const openStatusDialog = (service) => {
    setSelectedService(service);
    setStatusData({
      status: service.status,
      notes: ''
    });
    setStatusDialogOpen(true);
  };

  const openCommentDialog = (service) => {
    setSelectedService(service);
    setCommentData({
      comment: '',
      next_visit_date: ''
    });
    setCommentDialogOpen(true);
  };

  // Fetch full service detail (includes comments + payment info) and open view dialog
  const handleViewService = async (service) => {
    setSelectedService(service);
    setViewDialogOpen(true);
    try {
      const response = await api.get(`/services/${service.id}`);
      setSelectedService(response.data);
    } catch (error) {
      console.error('Failed to load service detail:', error);
    }
  };

  const openCloseServiceDialog = (service) => {
    setSelectedService(service);
    setClosureData({
      service_type_category: '',
      payment_status: '',
      payment_mode: '',
      payment_notes: '',
      free_service_notes: '',
      service_charge: '',
      due_date: '',
      collected_by: service?.assigned_to || user?.id || ''
    });
    setCloseServiceDialogOpen(true);
  };

  // Submit handlers
  const handleCreateService = async () => {
    if (!formData.customer_id || !formData.service_type || !formData.description || !formData.due_date || !formData.assigned_to) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // Validate service type specific requirements
    if (formData.service_type === 'AMC/MMC Service' && !formData.contract_id) {
      toast.error('Please select a contract for AMC/MMC Service');
      return;
    }
    
    if (formData.service_type === 'Stamping/License Renewal Service' && !formData.license_id) {
      toast.error('Please select a license for Stamping/License Renewal Service');
      return;
    }
    
    if (formData.service_type === 'In-Office Service' && !formData.device_reference_number) {
      toast.error('Please enter device reference number for In-Office Service');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare submit data
      const submitData = { ...formData };
      
      // Remove fields not relevant to the service type
      if (submitData.service_type !== 'AMC/MMC Service') {
        delete submitData.contract_id;
      }
      if (submitData.service_type !== 'Stamping/License Renewal Service') {
        delete submitData.license_id;
      }
      if (submitData.service_type !== 'In-Office Service') {
        delete submitData.device_reference_number;
      }
      
      await api.post('/services/', submitData);
      toast.success('Service created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedService || !statusData.status) {
      toast.error('Please select a status');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/services/${selectedService.id}/status`, {
        status: statusData.status,
        notes: statusData.notes
      });
      toast.success('Status updated successfully');
      setStatusDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedService || !commentData.comment) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      // Best-effort: capture location for better tracking, but allow comment as fallback if unavailable
      let locationParams = '';
      try {
        const location = await getCurrentLocation();
        locationParams = buildLocationParams(location);
      } catch (locErr) {
        console.warn('Location unavailable, submitting comment without it:', locErr?.message);
      }

      const url = locationParams
        ? `/services/${selectedService.id}/comments?${locationParams}`
        : `/services/${selectedService.id}/comments`;

      await api.post(url, {
        comment: commentData.comment,
        next_visit_date: commentData.next_visit_date || null
      });
      toast.success('Comment added successfully');
      setCommentDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseService = async () => {
    if (!selectedService) return;

    // Validate based on service type category
    if (!closureData.service_type_category) {
      toast.error('Please select service type (Free/Paid)');
      return;
    }

    // Free service validation
    if (closureData.service_type_category === 'Free') {
      if (!closureData.free_service_notes || closureData.free_service_notes.trim().length < 3) {
        toast.error('Notes are mandatory for free services');
        return;
      }
    }

    // Paid service validation
    if (closureData.service_type_category === 'Paid') {
      if (!closureData.payment_status) {
        toast.error('Please select payment status');
        return;
      }
      if (!closureData.service_charge || parseFloat(closureData.service_charge) <= 0) {
        toast.error('Service charge must be greater than 0');
        return;
      }
      if (closureData.payment_status === 'Paid' && !closureData.payment_mode) {
        toast.error('Please select payment mode');
        return;
      }
      if (closureData.payment_status === 'Pending' && !closureData.due_date) {
        toast.error('Due date is required for pending payments');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Build close data
      const closeData = {
        service_type_category: closureData.service_type_category
      };

      if (closureData.service_type_category === 'Free') {
        closeData.free_service_notes = closureData.free_service_notes;
      } else {
        closeData.payment_status = closureData.payment_status;
        closeData.service_charge = parseFloat(closureData.service_charge);
        closeData.payment_notes = closureData.payment_notes || '';
        
        if (closureData.payment_status === 'Paid') {
          closeData.payment_mode = closureData.payment_mode;
          closeData.collected_by = closureData.collected_by || selectedService.assigned_to;
        } else {
          closeData.due_date = closureData.due_date;
        }
      }

      await api.put(`/services/${selectedService.id}/close`, closeData);
      toast.success('Service closed successfully. Sent for approval.');
      setCloseServiceDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close service');
    } finally {
      setSubmitting(false);
    }
  };

  // Show access denied for Sales & Marketing
  if (!canAccessServices) {
    return (
      <div className="p-4 pb-24">
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <XCircle className="w-12 h-12 mx-auto mb-2 text-red-400" />
            <p>You do not have access to the Services module</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Service Management</h1>
          <p className="text-sm text-gray-500">{filteredServices.length} services</p>
        </div>
        <Button onClick={openCreateDialog} size="sm" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-4 gap-2">
        {SERVICE_STATUSES.map(status => {
          const count = statusCounts[status.value] || 0;
          const isSelected = statusFilter === status.value;
          return (
            <button
              key={status.value}
              onClick={() => setStatusFilter(isSelected ? 'all' : status.value)}
              className={`p-2 rounded-lg text-center transition-all ${
                isSelected 
                  ? `${status.color} ring-2 ring-offset-1 ring-gray-400` 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid={`status-filter-${status.value.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`text-lg font-bold ${isSelected ? '' : 'text-gray-700'}`}>{count}</div>
              <div className={`text-xs ${isSelected ? '' : 'text-gray-500'}`}>{status.value}</div>
            </button>
          );
        })}
      </div>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 min-w-[120px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {SERVICE_STATUSES.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Service Type Filter */}
          <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
            <SelectTrigger className="flex-1 min-w-[120px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {serviceTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Engineer Filter - Admin Only */}
          {isAdmin && (
            <Select value={engineerFilter} onValueChange={setEngineerFilter}>
              <SelectTrigger className="flex-1 min-w-[120px]">
                <SelectValue placeholder="All Engineers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Engineers</SelectItem>
                {engineers.map(eng => (
                  <SelectItem key={eng.id} value={eng.id}>{eng.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Services List */}
      {filteredServices.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Wrench className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No services found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredServices.map((service) => (
            <Card key={service.id} className="overflow-hidden" data-testid={`service-card-${service.id}`}>
              <CardContent className="p-4">
                {/* Header Row */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-sm font-semibold text-blue-600">{service.service_no}</span>
                    <Badge className={`ml-2 ${getStatusColor(service.status)}`}>
                      {service.status}
                    </Badge>
                    {service.priority !== 'Normal' && (
                      <Badge variant="outline" className={`ml-1 ${getPriorityColor(service.priority)}`}>
                        {service.priority}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {service.service_type}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{service.customer?.company_name}</span>
                  </div>
                  {service.customer?.mobile && (
                    <a href={`tel:${service.customer.mobile}`} className="flex items-center gap-2 text-blue-600">
                      <Phone className="w-4 h-4" />
                      <span>{service.customer.mobile}</span>
                    </a>
                  )}
                  {service.customer?.address && (
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="line-clamp-1">{service.customer.address}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>

                {/* Assigned Date */}
                {service.created_at && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                    <Clock className="w-3 h-3" />
                    <span>Assigned: {new Date(service.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                )}

                {/* Due Date & Assigned */}
                <div className="flex justify-between items-center text-sm mb-3">
                  <div className={`flex items-center gap-1 ${
                    isOverdue(service.due_date) && service.status !== 'Closed'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}>
                    <Calendar className="w-4 h-4" />
                    <span>Due: {service.due_date ? new Date(service.due_date).toLocaleDateString('en-IN') : 'N/A'}</span>
                    {isOverdue(service.due_date) && service.status !== 'Closed' && (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <User className="w-4 h-4" />
                    <span>{service.assigned_engineer?.name || 'Unassigned'}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleViewService(service)}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  
                  {canEditService(service) && service.status !== 'Closed' && service.status !== 'Cancelled' && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openStatusDialog(service)}
                        className="text-blue-600 border-blue-200"
                      >
                        <Settings className="w-4 h-4 mr-1" /> Status
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openCommentDialog(service)}
                        className="text-purple-600 border-purple-200"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" /> Comment
                      </Button>
                      
                      {service.status === 'On Process' && canEditService(service) && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openCloseServiceDialog(service)}
                          className="text-green-600 border-green-200"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Close
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Service Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book New Service</DialogTitle>
            <DialogDescription>Create a new service booking</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Selection */}
            <div className="relative">
              <Label>Customer *</Label>
              <Input 
                placeholder="Search customer by name or mobile..."
                value={customerSearchTerm}
                onChange={(e) => {
                  setCustomerSearchTerm(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
              />
              {showCustomerDropdown && customerSearchTerm && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.slice(0, 10).map(c => (
                      <div 
                        key={c.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                        onClick={() => handleCustomerSelect(c)}
                      >
                        <p className="font-medium">{c.company_name}</p>
                        <p className="text-sm text-gray-600">
                          {c.district && <span className="text-blue-600">{c.district}</span>}
                          {c.district && c.mobile && ' • '}
                          {c.mobile}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-gray-500">No customers found</div>
                  )}
                </div>
              )}
            </div>

            {/* Service Type */}
            <div>
              <Label>Service Type *</Label>
              <Select value={formData.service_type} onValueChange={handleServiceTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contract Selection for AMC/MMC Service */}
            {formData.service_type === 'AMC/MMC Service' && formData.customer_id && (
              <div>
                <Label>Select Contract *</Label>
                {customerContracts.length === 0 ? (
                  <p className="text-sm text-orange-600 mt-1">No active contracts found for this customer</p>
                ) : (
                  <Select value={formData.contract_id} onValueChange={(v) => setFormData(prev => ({ ...prev, contract_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerContracts.map(contract => (
                        <SelectItem key={contract.id} value={contract.id}>
                          <div>
                            <span>{contract.contract_no} - {contract.contract_type}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({contract.services_used}/{contract.services_included} used)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* License Selection for Stamping/License Service */}
            {formData.service_type === 'Stamping/License Renewal Service' && formData.customer_id && (
              <div>
                <Label>Select License (Expiring in 60 days) *</Label>
                {customerLicenses.length === 0 ? (
                  <p className="text-sm text-orange-600 mt-1">No licenses expiring in next 60 days</p>
                ) : (
                  <Select value={formData.license_id} onValueChange={(v) => setFormData(prev => ({ ...prev, license_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select license" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerLicenses.map(license => (
                        <SelectItem key={license.id} value={license.id}>
                          <div>
                            <span>{license.license_no} - {license.license_type}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              (Expires: {new Date(license.expiry_date).toLocaleDateString('en-IN')})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Device Reference for In-Office Service */}
            {formData.service_type === 'In-Office Service' && (
              <div>
                <Label>Device Reference Number *</Label>
                <Input 
                  value={formData.device_reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, device_reference_number: e.target.value.toUpperCase() }))}
                  placeholder="Enter unique device reference (e.g., DEV-001)"
                />
                <p className="text-xs text-gray-500 mt-1">Must be a unique reference number</p>
              </div>
            )}

            {/* Description */}
            <div>
              <Label>Service Description / Complaint *</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the service requirement..."
                rows={3}
              />
            </div>

            {/* Priority */}
            <div>
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date & Time */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Due Date *</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Due Time</Label>
                <Input 
                  type="time"
                  value={formData.due_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Assigned Engineer */}
            <div>
              <Label>Assigned Engineer *</Label>
              {isServiceEngineer ? (
                <div>
                  <Input 
                    value={user?.name || 'You (Auto-assigned)'}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-assigned to you</p>
                </div>
              ) : (
                <Select 
                  value={formData.assigned_to} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_to: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    {engineers.map(eng => (
                      <SelectItem key={eng.id} value={eng.id}>
                        {eng.name} - {eng.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateService} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Service Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service Details - {selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(selectedService.status)}>{selectedService.status}</Badge>
                <Badge variant="outline" className={getPriorityColor(selectedService.priority)}>
                  {selectedService.priority}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{selectedService.customer?.company_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Service Type</p>
                  <p className="font-medium">{selectedService.service_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Assigned To</p>
                  <p className="font-medium">{selectedService.assigned_engineer?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className={`font-medium ${isOverdue(selectedService.due_date) ? 'text-red-600' : ''}`}>
                    {selectedService.due_date ? new Date(selectedService.due_date).toLocaleDateString('en-IN') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{new Date(selectedService.created_at).toLocaleString('en-IN')}</p>
                </div>
                {selectedService.payment_status && (
                  <div>
                    <p className="text-gray-500">Payment</p>
                    <p className="font-medium">
                      {selectedService.payment_status}
                      {selectedService.bill_type && ` (${selectedService.bill_type})`}
                    </p>
                  </div>
                )}
              </div>

              {/* Service Amount - shown once closed */}
              {selectedService.status === 'Closed' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="service-amount-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-700 text-xs font-medium">Service Amount</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        ₹{Number(selectedService.service_charge || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        selectedService.payment_status === 'Paid' ? 'bg-green-600 text-white' :
                        selectedService.payment_status === 'Pending' ? 'bg-orange-500 text-white' :
                        'bg-blue-500 text-white'
                      }>
                        {selectedService.payment_status || 'N/A'}
                      </Badge>
                      {selectedService.payment_mode && (
                        <p className="text-xs text-gray-500 mt-1">via {selectedService.payment_mode}</p>
                      )}
                    </div>
                  </div>
                  {selectedService.payment_status === 'Pending' && selectedService.payment_due_date && (
                    <p className="text-xs text-orange-600 mt-2">
                      Payment due: {new Date(selectedService.payment_due_date).toLocaleDateString('en-IN')}
                    </p>
                  )}
                  {selectedService.payment_status === 'Free' && selectedService.free_service_notes && (
                    <p className="text-xs text-gray-600 mt-2">Note: {selectedService.free_service_notes}</p>
                  )}
                  {selectedService.payment_notes && (
                    <p className="text-xs text-gray-600 mt-1">{selectedService.payment_notes}</p>
                  )}
                </div>
              )}
              
              <div>
                <p className="text-gray-500 text-sm">Description</p>
                <p className="text-sm mt-1">{selectedService.description}</p>
              </div>

              {/* Comments/Updates */}
              {selectedService.comments && selectedService.comments.length > 0 && (
                <div data-testid="service-comments-section">
                  <p className="text-gray-500 text-sm mb-2 font-medium">Comments & Updates ({selectedService.comments.length})</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {[...selectedService.comments].reverse().map((comment, idx) => (
                      <div key={comment.id || idx} className="bg-gray-50 border border-gray-100 p-3 rounded-lg text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                            <User className="w-3 h-3 text-purple-600" />
                          </div>
                          <span className="font-medium text-gray-700 text-xs">
                            {comment.created_by_name || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto">
                            {comment.created_at ? new Date(comment.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                        {comment.next_visit_date && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                            <Calendar className="w-3 h-3" />
                            <span>Next visit: {new Date(comment.next_visit_date).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status - {selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status *</Label>
              <Select value={statusData.status} onValueChange={(v) => setStatusData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_STATUSES.filter(s => s.value !== 'Closed').map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={statusData.notes}
                onChange={(e) => setStatusData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add status update notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment - {selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comment *</Label>
              <Textarea 
                value={commentData.comment}
                onChange={(e) => setCommentData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Enter your comment or update..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Next Visit Date (Optional)</Label>
              <Input 
                type="date"
                value={commentData.next_visit_date}
                onChange={(e) => setCommentData(prev => ({ ...prev, next_visit_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddComment} disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Comment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Service Dialog - NEW WORKFLOW */}
      <Dialog open={closeServiceDialogOpen} onOpenChange={setCloseServiceDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Close Service - {selectedService?.service_no}</DialogTitle>
            <DialogDescription>Complete service closing details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Step 1: Service Type Category */}
            <div>
              <Label>Service Type *</Label>
              <Select 
                value={closureData.service_type_category} 
                onValueChange={(v) => setClosureData(prev => ({ 
                  ...prev, 
                  service_type_category: v,
                  payment_status: '',
                  payment_mode: '',
                  free_service_notes: '',
                  service_charge: '',
                  due_date: ''
                }))}
              >
                <SelectTrigger data-testid="mobile-service-type-select">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Paid">💰 Paid Service</SelectItem>
                  <SelectItem value="Free">🆓 Free Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* FREE SERVICE: Notes */}
            {closureData.service_type_category === 'Free' && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-green-800">Free Service Notes *</Label>
                <Textarea 
                  value={closureData.free_service_notes}
                  onChange={(e) => setClosureData(prev => ({ ...prev, free_service_notes: e.target.value }))}
                  placeholder="Enter reason for free service..."
                  rows={3}
                  className="mt-2"
                  data-testid="mobile-free-notes"
                />
                <p className="text-xs text-green-600 mt-2">This service will be marked as free and sent for approval.</p>
              </div>
            )}
            
            {/* PAID SERVICE */}
            {closureData.service_type_category === 'Paid' && (
              <>
                {/* Payment Status */}
                <div>
                  <Label>Payment Status *</Label>
                  <Select 
                    value={closureData.payment_status} 
                    onValueChange={(v) => setClosureData(prev => ({ 
                      ...prev, 
                      payment_status: v,
                      payment_mode: '',
                      due_date: ''
                    }))}
                  >
                    <SelectTrigger data-testid="mobile-payment-status-select">
                      <SelectValue placeholder="Select payment status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Paid">✅ Paid</SelectItem>
                      <SelectItem value="Pending">⏳ Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Service Charge */}
                <div>
                  <Label>Service Charge (₹) *</Label>
                  <Input 
                    type="number"
                    value={closureData.service_charge}
                    onChange={(e) => setClosureData(prev => ({ ...prev, service_charge: e.target.value }))}
                    placeholder="Enter service charge"
                    min="1"
                    data-testid="mobile-service-charge"
                  />
                </div>
                
                {/* If PAID: Payment Mode + Collected By */}
                {closureData.payment_status === 'Paid' && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                    <div>
                      <Label className="text-blue-800">Payment Mode *</Label>
                      <Select 
                        value={closureData.payment_mode} 
                        onValueChange={(v) => setClosureData(prev => ({ ...prev, payment_mode: v }))}
                      >
                        <SelectTrigger data-testid="mobile-payment-mode-select">
                          <SelectValue placeholder="Select payment mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">💵 Cash</SelectItem>
                          <SelectItem value="UPI">📱 UPI</SelectItem>
                          <SelectItem value="Bank Transfer">🏦 Bank Transfer</SelectItem>
                          <SelectItem value="Cheque">📄 Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-blue-800">Collected By</Label>
                      <Select 
                        value={closureData.collected_by} 
                        onValueChange={(v) => setClosureData(prev => ({ ...prev, collected_by: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select who collected" />
                        </SelectTrigger>
                        <SelectContent>
                          {engineers.map(eng => (
                            <SelectItem key={eng.id} value={eng.id}>
                              {eng.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {/* If PENDING: Due Date */}
                {closureData.payment_status === 'Pending' && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div>
                      <Label className="text-orange-800">Payment Due Date *</Label>
                      <Input 
                        type="date"
                        value={closureData.due_date}
                        onChange={(e) => setClosureData(prev => ({ ...prev, due_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-2"
                        data-testid="mobile-due-date"
                      />
                    </div>
                    <p className="text-xs text-orange-600 mt-2">Payment will be tracked until this due date.</p>
                  </div>
                )}
                
                {/* Payment Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    value={closureData.payment_notes}
                    onChange={(e) => setClosureData(prev => ({ ...prev, payment_notes: e.target.value }))}
                    placeholder="Add any additional notes..."
                    rows={2}
                    data-testid="mobile-payment-notes"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCloseServiceDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCloseService} 
              disabled={submitting || !closureData.service_type_category}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? 'Closing...' : 'Close Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileServices;
